import React, { useState, useEffect, useMemo } from 'react';
import { Bone, X, Loader2, AlertCircle } from 'lucide-react';
import {
  SymbolSpineConfig as SymbolSpineConfigType,
  SymbolSpineAnimations,
  CascadeSymbolCategory,
} from '@core/types/cascade-slot.types';
import { useProjectPanelStore } from '@store/projectPanelStore';
import { spineOffscreenRenderer } from '@renderer/services/SpineOffscreenRenderer';

type AnimationTriggerKey = keyof SymbolSpineAnimations;

interface SymbolSpineConfigProps {
  spineConfig?: SymbolSpineConfigType;
  category: CascadeSymbolCategory;
  onChange: (config: SymbolSpineConfigType) => void;
}

/** Animation triggers relevant to each symbol category. */
const CATEGORY_TRIGGERS: Record<CascadeSymbolCategory, AnimationTriggerKey[]> = {
  high: ['prepare', 'match'],
  normal: ['prepare', 'match'],
  scatter: ['idle', 'prepare', 'match', 'drop', 'nearwin'],
  wild: ['idle', 'prepare', 'match', 'spawn'],
  bonus: ['idle', 'match'],
};

/** Human-readable labels for animation triggers. */
const TRIGGER_LABELS: Record<AnimationTriggerKey, string> = {
  idle: 'Idle',
  prepare: 'Prepare',
  match: 'Match',
  drop: 'Drop',
  spawn: 'Spawn',
  nearwin: 'Near Win',
  highlight: 'Highlight',
};

/**
 * Given a skeleton file path, attempt to find sibling .atlas and .png files
 * from the project assets store.
 */
function findSiblingSpineFiles(skeletonPath: string): {
  atlasPath: string;
  texturePaths: string[];
} {
  const assets = useProjectPanelStore.getState().assets;
  // Derive the directory from the skeleton path
  const lastSlash = skeletonPath.lastIndexOf('/');
  const dir = lastSlash >= 0 ? skeletonPath.substring(0, lastSlash) : '';

  let atlasPath = '';
  const texturePaths: string[] = [];

  for (const asset of Object.values(assets)) {
    // Check if the asset lives in the same directory
    const assetLastSlash = asset.path.lastIndexOf('/');
    const assetDir = assetLastSlash >= 0 ? asset.path.substring(0, assetLastSlash) : '';
    if (assetDir !== dir) continue;

    const ext = asset.extension.toLowerCase();
    if (ext === '.atlas' && !atlasPath) {
      atlasPath = asset.path;
    } else if (ext === '.png') {
      texturePaths.push(asset.path);
    }
  }

  return { atlasPath, texturePaths };
}

const SpineDropZone: React.FC<{
  skeletonPath?: string;
  onAssign: (skeletonPath: string, atlasPath: string, texturePaths: string[]) => void;
  onClear: () => void;
}> = ({ skeletonPath, onAssign, onClear }) => {
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const assetId = e.dataTransfer.getData('text/plain');
    if (!assetId) return;

    const assets = useProjectPanelStore.getState().assets;
    const asset = assets[assetId];
    if (!asset) return;

    // Only accept spine assets
    if (asset.type !== 'spine') return;

    const ext = asset.extension.toLowerCase();
    if (ext !== '.skel' && ext !== '.json') return;

    // Auto-detect sibling .atlas and .png files
    const { atlasPath, texturePaths } = findSiblingSpineFiles(asset.path);
    onAssign(asset.path, atlasPath, texturePaths);
  };

  if (skeletonPath) {
    const fileName = skeletonPath.split('/').pop() ?? skeletonPath;
    return (
      <div className="flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 rounded p-1">
        <Bone size={14} className="text-purple-400 flex-shrink-0" />
        <span className="text-[10px] text-zinc-300 flex-1 truncate" title={skeletonPath}>
          {fileName}
        </span>
        <button
          className="p-0.5 rounded hover:bg-zinc-600 text-zinc-500 hover:text-red-400 flex-shrink-0"
          onClick={onClear}
          title="Clear spine asset"
        >
          <X size={10} />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center justify-center gap-1 border-2 border-dashed rounded py-2 transition-colors ${
        dragOver ? 'border-purple-500 bg-purple-500/10' : 'border-zinc-600 hover:border-zinc-500'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Bone size={12} className="text-zinc-500" />
      <span className="text-[10px] text-zinc-500">Drag .skel / .json here</span>
    </div>
  );
};

export const SymbolSpineConfig: React.FC<SymbolSpineConfigProps> = ({
  spineConfig,
  category,
  onChange,
}) => {
  const [animationNames, setAnimationNames] = useState<string[]>([]);
  const [skinNames, setSkinNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load metadata when spine config paths change
  useEffect(() => {
    if (!spineConfig?.skeletonPath || !spineConfig?.atlasPath) {
      setAnimationNames([]);
      setSkinNames([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    spineOffscreenRenderer
      .extractMetadata(
        spineConfig.skeletonPath,
        spineConfig.atlasPath,
        spineConfig.texturePaths
      )
      .then((metadata) => {
        if (cancelled) return;
        setAnimationNames(metadata.animations.map((a) => a.name));
        setSkinNames(metadata.skins);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[SymbolSpineConfig] Failed to load metadata:', err);
        setError('Failed to load animations');
        setAnimationNames([]);
        setSkinNames([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [spineConfig?.skeletonPath, spineConfig?.atlasPath, spineConfig?.texturePaths]);

  const triggers = useMemo(() => CATEGORY_TRIGGERS[category] ?? [], [category]);

  const handleAssign = (
    skeletonPath: string,
    atlasPath: string,
    texturePaths: string[]
  ) => {
    onChange({
      skeletonPath,
      atlasPath,
      texturePaths,
      animations: spineConfig?.animations ?? {},
      defaultSkin: spineConfig?.defaultSkin,
      timeScale: spineConfig?.timeScale ?? 1.0,
    });
  };

  const handleClear = () => {
    onChange({
      skeletonPath: '',
      atlasPath: '',
      texturePaths: [],
      animations: {},
      defaultSkin: undefined,
      timeScale: 1.0,
    });
  };

  const handleAnimationChange = (trigger: AnimationTriggerKey, value: string) => {
    if (!spineConfig) return;
    const animations: SymbolSpineAnimations = {
      ...spineConfig.animations,
      [trigger]: value || undefined,
    };
    onChange({ ...spineConfig, animations });
  };

  const handleSkinChange = (skin: string) => {
    if (!spineConfig) return;
    onChange({ ...spineConfig, defaultSkin: skin || undefined });
  };

  const handleTimeScaleChange = (value: number) => {
    if (!spineConfig) return;
    onChange({ ...spineConfig, timeScale: value });
  };

  const hasSkeletonPath = !!spineConfig?.skeletonPath;

  return (
    <div className="space-y-1.5">
      {/* Spine Asset Drop Zone */}
      <div className="space-y-1">
        <span className="text-[10px] text-zinc-500">Spine Asset</span>
        <SpineDropZone
          skeletonPath={spineConfig?.skeletonPath}
          onAssign={handleAssign}
          onClear={handleClear}
        />
        {spineConfig?.atlasPath && (
          <div className="text-[10px] text-zinc-600 truncate pl-1" title={spineConfig.atlasPath}>
            Atlas: {spineConfig.atlasPath.split('/').pop()}
          </div>
        )}
        {spineConfig?.texturePaths && spineConfig.texturePaths.length > 0 && (
          <div className="text-[10px] text-zinc-600 pl-1">
            Textures: {spineConfig.texturePaths.length} file(s)
          </div>
        )}
      </div>

      {/* Loading / Error feedback */}
      {loading && (
        <div className="flex items-center gap-1.5 px-1">
          <Loader2 size={12} className="text-purple-400 animate-spin" />
          <span className="text-[10px] text-purple-400">Loading animations...</span>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-1.5 px-1">
          <AlertCircle size={12} className="text-red-400 flex-shrink-0" />
          <span className="text-[10px] text-red-400">{error}</span>
        </div>
      )}

      {/* Animation Trigger Dropdowns */}
      {hasSkeletonPath && !loading && (
        <div className="space-y-1">
          <span className="text-[10px] text-zinc-500 font-semibold">Animation Triggers</span>
          {triggers.map((trigger) => (
            <div key={trigger} className="flex items-center gap-2">
              <span className="text-[10px] text-zinc-500 w-14">{TRIGGER_LABELS[trigger]}</span>
              <select
                className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-zinc-300 flex-1"
                value={spineConfig?.animations?.[trigger] ?? ''}
                onChange={(e) => handleAnimationChange(trigger, e.target.value)}
                disabled={!!error}
              >
                <option value="">(none)</option>
                {animationNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          ))}
          {error && (
            <span className="text-[10px] text-red-400 italic">(Failed to load animations)</span>
          )}
        </div>
      )}

      {/* Skin Selector */}
      {hasSkeletonPath && !loading && skinNames.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-500 w-14">Skin</span>
          <select
            className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-zinc-300 flex-1"
            value={spineConfig?.defaultSkin ?? ''}
            onChange={(e) => handleSkinChange(e.target.value)}
            disabled={!!error}
          >
            <option value="">(default)</option>
            {skinNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Time Scale */}
      {hasSkeletonPath && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-500 w-14">Time Scale</span>
          <input
            className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-zinc-300 w-16 text-center"
            type="number"
            step={0.1}
            min={0.1}
            max={5}
            value={spineConfig?.timeScale ?? 1.0}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) handleTimeScaleChange(Math.max(0.1, Math.min(5, v)));
            }}
          />
        </div>
      )}
    </div>
  );
};
